import { createContext, useContext, useLayoutEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  applyDefaultTheme,
  cssVariables,
  density as densityTokens,
  densityForGradeBand,
} from "@picnic-field/ui-tokens";
import type { Density, DensityTokens } from "@picnic-field/ui-tokens";
import type { SessionClaims } from "../types/session";

interface DensityCtx {
  density: Density;
  tokens: DensityTokens;
  /** 개발용 강제 전환(null 이면 세션 판정으로 되돌림) */
  setOverride: (d: Density | null) => void;
  override: Density | null;
}

const Ctx = createContext<DensityCtx | null>(null);

/**
 * 세션 → 밀도. SCR v0.6: 학생은 gradeBand(학년 1–3 → SE · 4–6 → SU · 미상 → SU),
 * 교사 화면(FS-01-04 · FS-11)은 SU/공통.
 */
export function densityForSession(s: Pick<SessionClaims, "role" | "gradeBand"> | null): Density {
  if (!s || s.role !== "student") return "su";
  return densityForGradeBand(s.gradeBand);
}

/** 개발 서버에서만 ?density=se|su 로 첫 화면 밀도를 강제한다(와이어 대조용). */
function devOverride(): Density | null {
  if (!import.meta.env.DEV || typeof window === "undefined") return null;
  const d = new URLSearchParams(window.location.search).get("density");
  return d === "se" || d === "su" ? d : null;
}

export function DensityProvider({ auto, children }: { auto: Density; children: ReactNode }) {
  const [override, setOverride] = useState<Density | null>(devOverride);
  const density = override ?? auto;

  // 기본 테마(Bench 와 같은 --color-* · --radius-*)를 먼저 세운다 — host(Campus) 토큰은 그 위에 덮인다.
  // --pf-* 는 이 토큰을 가리키는 별칭이라 재로드 없이 따라온다(Bench bridge/theme.ts 이식).
  useLayoutEffect(() => applyDefaultTheme(document.documentElement), []);

  useLayoutEffect(() => {
    const root = document.documentElement;
    for (const [k, v] of Object.entries(cssVariables(density))) root.style.setProperty(k, v);
    root.dataset.density = density;
  }, [density]);

  const value = useMemo(
    () => ({ density, tokens: densityTokens[density], setOverride, override }),
    [density, override],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useDensity(): DensityCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("DensityProvider 밖에서 useDensity 를 불렀습니다");
  return v;
}
